// ============================================================================
// SESSION TASKS SERVICE - Frontend API Client
// Handles API calls for session-scoped async multi-agent tasks
// ============================================================================

import { api } from './api';
import {
  SessionTaskTemplate,
  CreateSessionTaskInput,
  SessionTaskSummary,
  TaskExecution,
  TaskListFilters
} from '../types/session-task.types';

export const sessionTasksService = {
  /**
   * Get available task templates
   */
  async getTaskTemplates(): Promise<SessionTaskTemplate[]> {
    const response = await api.get('/api/task-templates');
    return response.data.templates;
  },

  /**
   * Create a new session task
   */
  async createTask(
    sessionId: string,
    input: CreateSessionTaskInput
  ): Promise<{ workflowId: string; executionId: string }> {
    const response = await api.post(`/api/sessions/${sessionId}/tasks`, input);
    return {
      workflowId: response.data.workflowId,
      executionId: response.data.executionId
    };
  },

  /**
   * List tasks for a session
   */
  async listTasks(
    sessionId: string,
    filters?: TaskListFilters
  ): Promise<SessionTaskSummary[]> {
    const params = new URLSearchParams();

    if (filters?.status && filters.status.length > 0) {
      params.append('status', filters.status.join(','));
    }

    if (filters?.category && filters.category.length > 0) {
      params.append('category', filters.category.join(','));
    }

    const response = await api.get(
      `/api/sessions/${sessionId}/tasks${params.toString() ? '?' + params.toString() : ''}`
    );

    return response.data.tasks;
  },

  /**
   * Get task execution details
   */
  async getTaskExecution(
    sessionId: string,
    workflowId: string,
    executionId: string
  ): Promise<TaskExecution> {
    const response = await api.get(
      `/api/sessions/${sessionId}/tasks/${workflowId}/executions/${executionId}`
    );
    return response.data.execution;
  },

  /**
   * Cancel a running task
   */
  async cancelTask(
    sessionId: string,
    workflowId: string,
    executionId: string
  ): Promise<void> {
    await api.post(
      `/api/sessions/${sessionId}/tasks/${workflowId}/executions/${executionId}/cancel`
    );
  },

  /**
   * Rerun a task with optional context overrides
   */
  async rerunTask(
    sessionId: string,
    workflowId: string,
    contextOverrides?: Record<string, any>
  ): Promise<{ executionId: string }> {
    const response = await api.post(
      `/api/sessions/${sessionId}/tasks/${workflowId}/rerun`,
      { contextOverrides }
    );
    return {
      executionId: response.data.executionId
    };
  },

  /**
   * Add a completed task's outputs to the session context (pending contributions)
   */
  async addTaskToContext(
    sessionId: string,
    workflowId: string
  ): Promise<{ addedCount: number }> {
    const response = await api.post(
      `/api/sessions/${sessionId}/tasks/${workflowId}/add-to-context`
    );
    return { addedCount: response.data.addedCount };
  },

  /**
   * Cancel a recurring task schedule
   */
  async cancelSchedule(
    sessionId: string,
    workflowId: string
  ): Promise<void> {
    await api.post(
      `/api/sessions/${sessionId}/tasks/${workflowId}/cancel-schedule`
    );
  },

  /**
   * List session tasks (alias for listTasks for clearer naming)
   */
  async listSessionTasks(
    sessionId: string,
    filters?: TaskListFilters
  ): Promise<SessionTaskSummary[]> {
    return this.listTasks(sessionId, filters);
  },

  /**
   * Toggle taggedForExport on a task
   */
  async setTaggedForExport(
    sessionId: string,
    workflowId: string,
    taggedForExport: boolean
  ): Promise<void> {
    await api.patch(
      `/api/sessions/${sessionId}/tasks/${workflowId}/tagged-for-export`,
      { taggedForExport }
    );
  }
};

// ============================================================================
// PROJECT TASKS - Project-scoped task API methods
// ============================================================================

export interface ProjectTaskSummary {
  taskId: string;
  projectId: string;
  taskName: string;
  taskCategory: 'thinking' | 'doing';
  templateId: string;
  status: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  createdBy: string;
  sourceSessionIds?: string[];
  sourceSessionName?: string;
  scope?: 'project' | 'session';
}

export interface ProjectTaskDetail extends ProjectTaskSummary {
  currentStepId?: string;
  currentStepName?: string;
  // Worker-specific fields
  executionMode?: 'n8n' | 'worker';
  ecsTaskArn?: string;
  progressMessages?: Array<{ timestamp: string; message: string }>;
  approvalPayload?: { summary: string; diff: string; requestedAt: string };
  approvalDecision?: 'approved' | 'rejected';
  approvalDecidedAt?: string;
  approvalDecidedBy?: string;
  stepResults?: Array<{
    stepId: string;
    stepName: string;
    status: string;
    startedAt?: string;
    completedAt?: string;
    output?: any;
    error?: string;
  }>;
  outputs?: Array<{
    type: string;
    content: any;
    source: string;
  }>;
  contextSnapshot?: Record<string, any>;
}

export interface TokenAllowance {
  maxTokensPerWindow: number;
  windowDurationMs: number;
  currentWindowStart: string;
  tokensUsedInWindow: number;
  enabled: boolean;
}

export interface TokenAllowanceCheck {
  allowed: boolean;
  remaining: number;
  resetsAt: string;
  enabled: boolean;
}

export interface CreateProjectTaskInput {
  taskTemplateId: string;
  taskName: string;
  taskCategory: 'thinking' | 'doing';
  contextSnapshot: {
    transcriptEntryIds?: string[];
    requirementIds?: string[];
    personaIds?: string[];
    avatarIds?: string[];
    customContext?: Record<string, any>;
  };
  sourceSessionIds?: string[];
}

export const projectTasksService = {
  /**
   * Create a new project task
   */
  async createTask(
    projectId: string,
    input: CreateProjectTaskInput
  ): Promise<{ taskId: string }> {
    const response = await api.post(`/api/projects/${projectId}/tasks`, input);
    return { taskId: response.data.taskId };
  },

  /**
   * List tasks for a project
   */
  async listTasks(
    projectId: string,
    filters?: { status?: string[]; category?: string[] }
  ): Promise<ProjectTaskSummary[]> {
    const params = new URLSearchParams();
    if (filters?.status && filters.status.length > 0) {
      params.append('status', filters.status.join(','));
    }
    if (filters?.category && filters.category.length > 0) {
      params.append('category', filters.category.join(','));
    }
    const response = await api.get(
      `/api/projects/${projectId}/tasks${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data.tasks;
  },

  /**
   * Get task details
   */
  async getTask(
    projectId: string,
    taskId: string
  ): Promise<ProjectTaskDetail> {
    const response = await api.get(`/api/projects/${projectId}/tasks/${taskId}`);
    return response.data.task;
  },

  /**
   * Cancel a running task
   */
  async cancelTask(
    projectId: string,
    taskId: string
  ): Promise<void> {
    await api.post(`/api/projects/${projectId}/tasks/${taskId}/cancel`);
  },

  /**
   * Rerun a task
   */
  async rerunTask(
    projectId: string,
    taskId: string
  ): Promise<{ taskId: string }> {
    const response = await api.post(`/api/projects/${projectId}/tasks/${taskId}/rerun`);
    return { taskId: response.data.taskId };
  },

  /**
   * Delete a single non-running task
   */
  async deleteTask(
    projectId: string,
    taskId: string
  ): Promise<void> {
    await api.delete(`/api/projects/${projectId}/tasks/${taskId}`);
  },

  /**
   * Bulk cancel running tasks
   */
  async bulkCancel(
    projectId: string,
    taskIds: string[]
  ): Promise<{ cancelled: number; errors: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/tasks/bulk-cancel`, { taskIds });
    return response.data;
  },

  /**
   * Bulk delete non-running tasks
   */
  async bulkDelete(
    projectId: string,
    taskIds: string[]
  ): Promise<{ deleted: number; errors: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/tasks/bulk-delete`, { taskIds });
    return response.data;
  },

  /**
   * Approve a worker task awaiting human approval
   */
  async approveTask(
    projectId: string,
    taskId: string,
    reason?: string
  ): Promise<void> {
    await api.post(`/api/projects/${projectId}/tasks/${taskId}/approve`, { reason });
  },

  /**
   * Reject a worker task awaiting human approval
   */
  async rejectTask(
    projectId: string,
    taskId: string,
    reason?: string
  ): Promise<void> {
    await api.post(`/api/projects/${projectId}/tasks/${taskId}/reject`, { reason });
  },

  /**
   * Get token allowance for a project
   */
  async getTokenAllowance(
    projectId: string
  ): Promise<{ allowance: TokenAllowance | null; check: TokenAllowanceCheck }> {
    const response = await api.get(`/api/projects/${projectId}/token-allowance`);
    return { allowance: response.data.allowance, check: response.data.check };
  },

  /**
   * Set token allowance for a project (admin only)
   */
  async setTokenAllowance(
    projectId: string,
    config: { maxTokensPerWindow: number; windowDurationMs: number; enabled: boolean }
  ): Promise<void> {
    await api.put(`/api/projects/${projectId}/token-allowance`, config);
  }
};

// ============================================================================
// CUSTOMER RESEARCH - Research + graph query API methods
// ============================================================================

export interface LaunchResearchInput {
  customerName: string;
  customerWebsite?: string;
  organisationType?: string;
  focusAreas?: string[];
}

export interface GraphNode {
  id: string;
  type: string;
  labels: string[];
  name: string;
  [key: string]: any;
}

export interface GraphEdge {
  id: string;
  source: string;
  target: string;
  type: string;
  [key: string]: any;
}

export interface GraphData {
  nodes: GraphNode[];
  edges: GraphEdge[];
  nodeCount: number;
  edgeCount: number;
  context?: string;
}

export const customerResearchService = {
  /**
   * Launch a deep customer research task
   */
  async launchResearch(
    projectId: string,
    input: LaunchResearchInput
  ): Promise<{ taskId: string }> {
    const response = await api.post(`/api/projects/${projectId}/research`, input);
    return { taskId: response.data.taskId };
  },

  /**
   * Query the knowledge graph for a project
   */
  async getGraphData(
    projectId: string,
    options?: { query?: string; entityType?: string; limit?: number; sessionId?: string }
  ): Promise<GraphData> {
    const params = new URLSearchParams();
    if (options?.query) params.append('query', options.query);
    if (options?.entityType) params.append('entityType', options.entityType);
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.sessionId) params.append('sessionId', options.sessionId);
    const response = await api.get(
      `/api/projects/${projectId}/research/graph${params.toString() ? '?' + params.toString() : ''}`
    );
    return response.data;
  },

  /**
   * Get formatted graph context summary for display
   */
  async getGraphContext(projectId: string): Promise<string> {
    const response = await api.get(`/api/projects/${projectId}/research/graph/context`);
    return response.data.context || '';
  },

  /**
   * Manually trigger graph sync from a research task
   */
  async triggerGraphSync(
    projectId: string,
    sourceTaskId: string
  ): Promise<{ taskId: string }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/sync`, { sourceTaskId });
    return { taskId: response.data.taskId };
  },

  /**
   * Backfill all existing session data into the Neo4j graph
   */
  async backfillSessionsToGraph(
    projectId: string
  ): Promise<{ message: string; stats: { sessions: number; contributions: number; userStories: number; personas: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-sessions`);
    return response.data;
  },

  /**
   * Clear all Neo4j graph data for a project
   */
  async clearGraph(
    projectId: string
  ): Promise<{ message: string; deletedNodes: number }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/clear`);
    return response.data;
  },

  async deleteGraphNodes(
    projectId: string,
    nodeIds: string[]
  ): Promise<{ deletedNodes: number }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/delete-nodes`, { nodeIds });
    return response.data;
  },

  async mergeNodes(
    projectId: string,
    keepNodeId: string,
    mergeNodeId: string,
    mergedName?: string,
    mergedDescription?: string
  ): Promise<void> {
    await api.post(`/api/projects/${projectId}/research/graph/merge-nodes`, {
      keepNodeId,
      mergeNodeId,
      ...(mergedName ? { mergedName } : {}),
      ...(mergedDescription ? { mergedDescription } : {})
    });
  },

  async findDuplicates(
    projectId: string,
    sessionId: string
  ): Promise<{ groups: Array<{ nodes: GraphNode[]; suggestedName: string; suggestedDescription: string }> }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/find-duplicates`, { sessionId });
    return response.data;
  },

  async updateNodeName(
    projectId: string,
    nodeId: string,
    name: string
  ): Promise<void> {
    await api.post(`/api/projects/${projectId}/research/graph/update-node`, { nodeId, name });
  },

  async backfillSession(
    projectId: string,
    sessionId: string
  ): Promise<{ message: string; stats: Record<string, number>; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-session`, { sessionId });
    return response.data;
  },

  async syncParticipants(
    projectId: string,
    sessionId: string,
    attendees: Array<{ id: string; name: string; role: string; organization: string }>,
    facilitators: Array<{ id: string; name: string; role: string; organization: string }>,
    removeParticipantIds?: string[]
  ): Promise<void> {
    await api.post(`/api/projects/${projectId}/research/graph/sync-participants`, {
      sessionId,
      attendees,
      facilitators,
      removeParticipantIds
    });
  },

  /**
   * Rebuild the graph from all completed research tasks
   */
  async rebuildGraph(
    projectId: string
  ): Promise<{ message: string; stats: { tasksFound: number; syncsTriggered: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/rebuild`);
    return response.data;
  },

  async backfillIdeation(
    projectId: string
  ): Promise<{ message: string; stats: { campaigns: number; submissions: number; syncsTriggered: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-ideation`);
    return response.data;
  },

  async backfillTaskOutputs(
    projectId: string
  ): Promise<{ message: string; stats: { tasksFound: number; syncsTriggered: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-task-outputs`);
    return response.data;
  },

  async backfillGovernance(
    projectId: string
  ): Promise<{ message: string; stats: { sessionsChecked: number; syncsTriggered: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-governance`);
    return response.data;
  },

  async backfillProject(
    projectId: string
  ): Promise<{ message: string; stats: { syncsTriggered: number } }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-project`);
    return response.data;
  },

  async backfillDeviceImages(
    projectId: string
  ): Promise<{ message: string; stats: { sessionsChecked: number; syncsTriggered: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-device-images`);
    return response.data;
  },

  async backfillDecisions(
    projectId: string
  ): Promise<{ message: string; stats: { sessionsChecked: number; decisionsFound: number; syncsTriggered: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-decisions`);
    return response.data;
  },

  async backfillBuilds(
    projectId: string
  ): Promise<{ message: string; stats: { buildTasksFound: number; syncsTriggered: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-builds`);
    return response.data;
  },

  async backfillAvatars(
    projectId: string
  ): Promise<{ message: string; stats: { sessionsProcessed: number; contributionsQueued: number }; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-avatars`);
    return response.data;
  },

  async backfillProjectAvatars(
    projectId: string
  ): Promise<{ message: string; count: number; errors?: string[] }> {
    const response = await api.post(`/api/projects/${projectId}/research/graph/backfill-project-avatars`);
    return response.data;
  },
};

export const sessionService = {
  async updateAttendees(
    sessionId: string,
    attendees: Array<{ id: string; name: string; role: string; organization: string }>,
    facilitators: Array<{ id: string; name: string; role: string; organization: string }>
  ): Promise<void> {
    await api.put(`/api/sessions/${sessionId}/attendees`, { attendees, facilitators });
  },

  async updateDesignContext(sessionId: string, designContext: string): Promise<void> {
    await api.patch(`/api/sessions/${sessionId}/design-context`, { designContext });
  },

  async updateImageStyle(sessionId: string, imageStyle: string): Promise<void> {
    await api.patch(`/api/sessions/${sessionId}/image-style`, { imageStyle });
  },

  async attributeTranscriptEntry(
    sessionId: string,
    entryId: string,
    timestamp: string,
    attributedTo: Array<{ id: string; name: string; role: string; organization: string }>,
    projectId?: string,
    text?: string
  ): Promise<void> {
    await api.patch(`/api/sessions/${sessionId}/transcript/${entryId}/attribution`, {
      attributedTo,
      timestamp,
      projectId,
      text
    });
  },
};

export default sessionTasksService;
